import { useCallback, useEffect, useRef, useState } from "react";

export type WalkthroughStepType =
	| "intro"
	| "concept"
	| "try"
	| "recap";

export interface WalkthroughStep {
	type: WalkthroughStepType;
	title: string;
	body: string;
	code?: string;
	hint?: string;
}

export interface Walkthrough {
	title: string;
	lessonFile: string;
	description: string;
	estimatedTime: string;
	steps: WalkthroughStep[];
}

interface UseWalkthroughOptions {
	onLoadCode: (code: string, title: string) => void;
}

const PROGRESS_KEY = "pyblaze.walkthrough.progress";

function readProgress(): Record<string, number> {
	try {
		const raw = localStorage.getItem(PROGRESS_KEY);
		return raw ? JSON.parse(raw) : {};
	} catch {
		return {};
	}
}

function writeProgress(progress: Record<string, number>) {
	try {
		localStorage.setItem(PROGRESS_KEY, JSON.stringify(progress));
	} catch {
		/* ignore quota errors */
	}
}

export function useWalkthrough({ onLoadCode }: UseWalkthroughOptions) {
	const [available, setAvailable] = useState<Set<string>>(new Set());
	const [active, setActive] = useState<Walkthrough | null>(null);
	const [stepIndex, setStepIndex] = useState(0);
	const [loading, setLoading] = useState(false);
	const cacheRef = useRef<Map<string, Walkthrough>>(new Map());

	// Load the manifest once so we know which lessons have walkthroughs.
	useEffect(() => {
		let cancelled = false;
		fetch("/examples/walkthroughs/manifest.json")
			.then((r) => (r.ok ? r.json() : null))
			.then((data) => {
				if (cancelled || !data?.walkthroughs) return;
				setAvailable(new Set<string>(data.walkthroughs));
			})
			.catch(() => {
				/* manifest missing is fine — no walkthroughs available */
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const start = useCallback(
		async (lessonFile: string) => {
			setLoading(true);
			try {
				let walkthrough = cacheRef.current.get(lessonFile);
				if (!walkthrough) {
					const slug = lessonFile.replace(/\.py$/, "");
					const res = await fetch(`/examples/walkthroughs/${slug}.json`);
					if (!res.ok) throw new Error("walkthrough not found");
					walkthrough = (await res.json()) as Walkthrough;
					cacheRef.current.set(lessonFile, walkthrough);
				}
				const savedIndex = readProgress()[lessonFile] ?? 0;
				const safeIndex = Math.min(
					Math.max(0, savedIndex),
					walkthrough.steps.length - 1,
				);
				setActive(walkthrough);
				setStepIndex(safeIndex);

				// If the resumed step has code, prime the editor with it
				const step = walkthrough.steps[safeIndex];
				if (step?.code) {
					onLoadCode(step.code, walkthrough.title);
				}
			} catch (err) {
				console.warn("Failed to start walkthrough:", err);
			} finally {
				setLoading(false);
			}
		},
		[onLoadCode],
	);

	const close = useCallback(() => {
		setActive(null);
		setStepIndex(0);
	}, []);

	const goTo = useCallback(
		(index: number) => {
			if (!active) return;
			const clamped = Math.max(0, Math.min(index, active.steps.length - 1));
			setStepIndex(clamped);
			const progress = readProgress();
			progress[active.lessonFile] = clamped;
			writeProgress(progress);
		},
		[active],
	);

	const next = useCallback(() => {
		if (!active) return;
		goTo(stepIndex + 1);
	}, [active, stepIndex, goTo]);

	const prev = useCallback(() => {
		if (!active) return;
		goTo(stepIndex - 1);
	}, [active, stepIndex, goTo]);

	const loadStepCode = useCallback(() => {
		if (!active) return;
		const step = active.steps[stepIndex];
		if (step?.code) {
			onLoadCode(step.code, active.title);
		}
	}, [active, stepIndex, onLoadCode]);

	const hasWalkthrough = useCallback(
		(lessonFile: string) => available.has(lessonFile),
		[available],
	);

	return {
		active,
		stepIndex,
		loading,
		start,
		close,
		next,
		prev,
		goTo,
		loadStepCode,
		hasWalkthrough,
	};
}
